import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import LoadingSpinner from "@/components/LoadingSpinner";

// ── Types ──────────────────────────────────────────────────────────────────

interface ResolvedSubscription {
  subscriptionId: string;
  offerId: string;
  planId: string;
  quantity: number;
  purchaserEmail: string | null;
  purchaserTenantId: string | null;
  alreadyProvisioned: boolean;
  siteId?: number;
}

interface Environment {
  displayName: string;
  instanceUrl: string;
}

type Step = 1 | 2 | 3 | 4;

// ── Helpers ────────────────────────────────────────────────────────────────

const ADMIN_API = (import.meta.env.VITE_ADMIN_API_URL as string) ?? "";

async function adminPost(path: string, body: unknown): Promise<Response> {
  return fetch(`${ADMIN_API}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
}

function planLabel(planId: string): string {
  const map: Record<string, string> = {
    "velaro-starter": "Velaro Starter",
    "velaro-professional": "Velaro Professional",
    "velaro-enterprise": "Velaro Enterprise"
  };
  return map[planId] ?? planId;
}

// ── Step indicator ─────────────────────────────────────────────────────────

const STEPS = [
  { n: 1 as Step, label: "Connect" },
  { n: 2 as Step, label: "Select Environment" },
  { n: 3 as Step, label: "Activate" },
  { n: 4 as Step, label: "Done" }
];

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                current >= s.n
                  ? "text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
              style={current >= s.n ? { backgroundColor: "var(--color-primary)" } : undefined}
            >
              {current > s.n ? "✓" : s.n}
            </div>
            <span
              className={`text-sm ${current === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Error banner ───────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
      {message}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function MarketplaceActivation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [step, setStep] = useState<Step>(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Step 1 data
  const [subscription, setSubscription] = useState<ResolvedSubscription | null>(null);

  // Step 2 data — consent + environments
  const [consentWindow, setConsentWindow] = useState<Window | null>(null);
  const [delegatedToken, setDelegatedToken] = useState<string | null>(null);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<string>("");

  // Step 3 data — activation
  const [activating, setActivating] = useState(false);

  // ── Step 1: resolve token on mount ──────────────────────────────────────

  useEffect(() => {
    if (!token) {
      setError("Invalid activation link — no marketplace token found.");
      return;
    }
    resolveToken(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function resolveToken(tok: string) {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminPost("AppSource/resolve", { token: tok });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(`Failed: HTTP ${resp.status} — ${body.error ?? resp.statusText}`);
        return;
      }
      const data = await resp.json() as ResolvedSubscription;
      setSubscription(data);
    } catch (err) {
      setError(`Failed to reach the activation server: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: open OAuth consent popup ────────────────────────────────────

  function openConsentPopup() {
    if (!subscription) return;

    // Build the Microsoft multi-tenant admin consent URL.
    // The redirect_uri is handled server-side; we open the Microsoft page directly.
    // After consent, Microsoft redirects to our server endpoint which returns
    // the auth code. We listen for the popup to redirect to our callback page.
    const clientId = (import.meta.env.VITE_APPSOURCE_MULTITENANT_CLIENT_ID as string) ?? "";
    const redirectUri = encodeURIComponent(
      `${ADMIN_API}/AppSource/consent-callback`
    );
    const consentUrl =
      `https://login.microsoftonline.com/${subscription.purchaserTenantId ?? "common"}/adminconsent` +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&state=${encodeURIComponent(subscription.subscriptionId)}`;

    const popup = window.open(consentUrl, "d365consent", "width=600,height=700");
    setConsentWindow(popup);
    setError(null);
    setLoading(true);

    // Poll for the popup to close (consent completed or cancelled)
    const poll = setInterval(() => {
      if (popup?.closed) {
        clearInterval(poll);
        setLoading(false);
        // The server-side consent-callback handled code exchange.
        // We now POST to get environments using what the server stored.
        // In the AppSource flow, we call consent-callback with the auth code
        // that Microsoft gave the popup's redirect. Since it's server-side,
        // we need the frontend to call /AppSource/consent-callback with the
        // code that was appended to the popup URL before closing.
        // For this implementation we use postMessage from the popup redirect page.
      }
    }, 500);
  }

  // Listen for postMessage from the OAuth popup callback page
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      const { type, authCode, customerTenantId, error: msgError } = event.data ?? {};
      if (type !== "appsource-consent") return;

      if (msgError) {
        setError(`Consent failed: ${msgError}`);
        setLoading(false);
        return;
      }
      if (authCode && customerTenantId) {
        exchangeConsentCode(authCode, customerTenantId);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  async function exchangeConsentCode(authCode: string, customerTenantId: string) {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminPost("AppSource/consent-callback", {
        authCode,
        customerTenantId
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(`Failed: HTTP ${resp.status} — ${body.error ?? resp.statusText}`);
        return;
      }
      const data = await resp.json() as { environments: Environment[]; delegatedToken: string };
      setDelegatedToken(data.delegatedToken ?? null);
      const envs = Array.isArray(data.environments) ? data.environments : [];
      setEnvironments(envs);
      if (envs.length > 0) setSelectedEnv(envs[0].instanceUrl);
      setStep(2);
    } catch (err) {
      setError(`Consent exchange failed: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 3: provision D365 app user ─────────────────────────────────────

  async function handleProvision() {
    if (!selectedEnv || !delegatedToken) {
      setError("Please select a Dynamics 365 environment.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await adminPost("AppSource/provision-d365", {
        delegatedToken,
        instanceUrl: selectedEnv
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(`Failed: HTTP ${resp.status} — ${body.error ?? resp.statusText}`);
        return;
      }
      setStep(3);
    } catch (err) {
      setError(`Provisioning failed: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 4: activate subscription ───────────────────────────────────────

  async function handleActivate() {
    if (!subscription) return;
    setActivating(true);
    setError(null);
    try {
      const selectedEnvObj = environments.find((e) => e.instanceUrl === selectedEnv);
      const resp = await adminPost("AppSource/activate", {
        marketplaceSubscriptionId: subscription.subscriptionId,
        offerId: subscription.offerId,
        planId: subscription.planId,
        quantity: subscription.quantity,
        purchaserEmail: subscription.purchaserEmail,
        purchaserTenantId: subscription.purchaserTenantId,
        instanceUrl: selectedEnv,
        orgName: selectedEnvObj?.displayName ?? ""
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(`Failed: HTTP ${resp.status} — ${body.error ?? resp.statusText}`);
        return;
      }
      setStep(4);
    } catch (err) {
      setError(`Activation failed: ${String(err)}`);
    } finally {
      setActivating(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  // No token → immediate error, no loading
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">✕</span>
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Invalid activation link</h2>
          <p className="text-sm text-slate-500">
            Invalid activation link — no marketplace token found.
          </p>
          <p className="text-sm text-slate-400 mt-4">
            If you came from Microsoft AppSource, please try clicking the link again or contact{" "}
            <a href="mailto:support@velaro.com" className="text-blue-600 underline">
              support@velaro.com
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      {/* Velaro brand header */}
      <div className="mb-8 text-center">
        <div className="text-2xl font-bold text-slate-800 mb-1">Velaro</div>
        <div className="text-sm text-slate-500">AI Customer Engagement for Dynamics 365</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-xl w-full">
        <StepIndicator current={step} />

        {/* ── Step 1: Welcome / resolve ── */}
        {step === 1 && (
          <>
            <h2 className="text-xl font-semibold text-slate-800 mb-1">Welcome to Velaro</h2>
            <p className="text-sm text-slate-500 mb-6">
              Let&apos;s connect your Dynamics 365 environment. This takes about 60 seconds.
            </p>

            {loading && (
              <div className="flex items-center gap-3 text-sm text-slate-500 mb-6">
                <LoadingSpinner />
                Verifying your purchase…
              </div>
            )}

            {error && <ErrorBanner message={error} />}

            {subscription && !loading && (
              <>
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 mb-6 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Plan</span>
                    <span className="font-medium text-slate-800">{planLabel(subscription.planId)}</span>
                  </div>
                  {subscription.purchaserEmail && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Purchased by</span>
                      <span className="font-medium text-slate-800">{subscription.purchaserEmail}</span>
                    </div>
                  )}
                  {subscription.quantity > 1 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Seats</span>
                      <span className="font-medium text-slate-800">{subscription.quantity}</span>
                    </div>
                  )}
                </div>

                {subscription.alreadyProvisioned ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-sm text-green-700">
                    This subscription is already active.{" "}
                    <Link to="/" className="underline font-medium">
                      Go to your dashboard →
                    </Link>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mb-6">
                    Click below to connect your Microsoft Dynamics 365 environment. You&apos;ll be
                    asked to grant Velaro access to your organization.
                  </p>
                )}

                {!subscription.alreadyProvisioned && (
                  <div className="flex justify-end">
                    <button
                      className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
                      style={{ backgroundColor: "var(--color-primary)" }}
                      onClick={openConsentPopup}
                      disabled={loading}
                    >
                      Connect Microsoft Dynamics 365 →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Step 2: Select environment ── */}
        {step === 2 && (
          <>
            <h2 className="text-xl font-semibold text-slate-800 mb-1">Select your Dynamics 365 environment</h2>
            <p className="text-sm text-slate-500 mb-6">
              Choose the environment where Velaro should create its application user.
            </p>

            {error && <ErrorBanner message={error} />}

            {environments.length === 0 ? (
              <p className="text-sm text-slate-400 mb-6">No environments found in your organization.</p>
            ) : (
              <div className="space-y-2 mb-6">
                {environments.map((env) => (
                  <label
                    key={env.instanceUrl}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      selectedEnv === env.instanceUrl
                        ? "border-blue-400 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="environment"
                      value={env.instanceUrl}
                      checked={selectedEnv === env.instanceUrl}
                      onChange={() => setSelectedEnv(env.instanceUrl)}
                      className="accent-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-800">{env.displayName}</div>
                      <div className="text-xs text-slate-400">{env.instanceUrl}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <LoadingSpinner />
                  Connecting…
                </div>
              ) : (
                <button
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onClick={handleProvision}
                  disabled={!selectedEnv}
                >
                  Connect this environment →
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Step 3: Activate ── */}
        {step === 3 && (
          <>
            <h2 className="text-xl font-semibold text-slate-800 mb-1">Ready to activate</h2>
            <p className="text-sm text-slate-500 mb-6">
              Velaro has been configured in your Dynamics 365 environment. Click below to activate your
              subscription and start Microsoft billing.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 space-y-1 text-sm text-green-700">
              <div className="flex items-center gap-2 font-medium">
                <span>✓</span> Connected to {environments.find((e) => e.instanceUrl === selectedEnv)?.displayName ?? selectedEnv}
              </div>
              <div className="flex items-center gap-2 font-medium">
                <span>✓</span> Velaro application user created in Dynamics 365
              </div>
            </div>

            {error && <ErrorBanner message={error} />}

            <div className="flex justify-end">
              {activating ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <LoadingSpinner />
                  Activating…
                </div>
              ) : (
                <button
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onClick={handleActivate}
                >
                  Activate subscription →
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Step 4: Done ── */}
        {step === 4 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-green-500 text-3xl">✓</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">You&apos;re all set!</h2>
            <p className="text-sm text-slate-500 mb-6">
              Velaro is active and connected to your Dynamics 365 environment.
            </p>
            <div className="text-left bg-slate-50 rounded-lg border border-slate-200 p-4 mb-6 space-y-2 text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-2">Next steps:</p>
              <div className="flex items-start gap-2">
                <span className="font-bold">1.</span>
                <span>Add the Velaro chat widget to your website</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-bold">2.</span>
                <span>Set up your first AI workflow</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-bold">3.</span>
                <span>Invite your team</span>
              </div>
            </div>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              Open Velaro Dashboard →
            </Link>
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-slate-400">
        Need help?{" "}
        <a href="mailto:support@velaro.com" className="underline">
          Contact support
        </a>
      </p>
    </div>
  );
}
